/**
 * Maps a MuviHub cart `totals` into the legacy `calculatedValues` field
 * names the rest of the app already reads. Points fields come directly
 * from the cart totals (Bug 7905).
 */
export function mapCartTotals(totals = {}) {
	const totalConvenienceFee = totals.totalServiceCharge || 0
	const totalServiceFee = totals.totalFees || 0
	const totalToll = totals.totalTolls || 0

	return {
		...totals,
		totalFare: totals.totalFare || 0,
		totalConvenienceFee,
		totalServiceFee,
		totalToll,
		// "Taxa de Servico" (totalConvenienceFee) is shown as its own line —
		// don't double-count it here, this is only tolls + generic fees.
		totalFeeSummary: totalServiceFee + totalToll,
		totalInsuranceValue: totals.totalInsurance || 0,
		totalDiscount: totals.totalDiscount || 0,
		hasFamilyDiscount: totals.discountType === 'DESCONTO FAMILIA',
		totalOrderValue: totals.totalPayable || 0,
		totalInterest: totals.totalInterestCharges || 0,
		totalOther: 0,
		totalBpeValue: 0,
		totalPointsBpe: totals.totalPointsBpe || 0,
		totalPointsForConvenienceFee: totals.totalPointsForConvenienceFee || 0,
		totalInsurancePoints: totals.totalInsurancePoints || 0,
		totalPointsUsed: totals.totalPointsUsed || 0,
		// Valor exibido na linha "Subtotal" e se as taxas ja estao dentro dele.
		// Na compra em dinheiro o subtotal e a tarifa e as taxas sao somadas a
		// parte; a compra com pontos sobrescreve os dois (ver getCalculatedValues).
		subtotalValue: totals.totalFare || 0,
		feesIncludedInSubtotal: false,
	}
}

/**
 * Resolves the `rebookingContext` to feed into `setCalculatedValues` (bug: cobrança de remarcação
 * classificada como estorno). `setCalculatedValues` REPLACES `calculatedValues` wholesale rather
 * than merging, so every call site that rebuilds it (coupon apply, deselecting return seats, ...)
 * must explicitly carry `rebookingContext` forward or it silently disappears. Once it's gone,
 * payment-step falls back to comparing the original ticket's raw value against a totalOrderValue
 * that (for a rebooking cart) already comes net of that same value from the backend
 * (`CartMapper.ToTotalsDto`, MuviHub) — misclassifying a real charge as a refund and hiding the
 * card form.
 * @param {{operationType: string, amountDue: number, originalTicketValue: number}|null} [rebookingContext]
 * The context from the cart response that triggered this recalculation, if any.
 * @param {object|null} [previous] The current `calculatedValues.rebookingContext`, used when this
 * particular response didn't carry a fresh one (e.g. a non-rebooking cart call, or a client that
 * doesn't include the field).
 * @return {object|null}
 */
export function resolveRebookingContext(rebookingContext, previous = null) {
	return rebookingContext || previous || null
}

/**
 * Fills in the per-ticket fields from the seat object built during booking.
 * Points come from userPricing.paymentOptions[selected].perSeat (Bug 7905).
 */
export function enrichTicket(seat) {
	const hasPoints = seat.bpePointsUsed > 0
	// When paying with points, pointsMoneyCost is the cash portion for this
	// seat (moneyCost from perSeat). Use it as the displayed fare instead
	// of the full total.
	const fare = hasPoints && seat.pointsMoneyCost != null
		? seat.pointsMoneyCost
		: seat.price && seat.price.value

	return {
		...seat,
		fare,
		bpePointsUsed: seat.bpePointsUsed || 0,
		isPromotionalSeat: !!(seat.discountForPromotionBusSeat > 0),
	}
}

/**
 * Names the payment modality a price option represents, from its `{ value,
 * points }` shape — the same one `mapPaymentOptions` produces from the API's
 * `paymentOptions`. Gives the price cards a semantic identity instead of the
 * positional `price-${position}` class.
 * @param {{value: number, points: number}} price A price option
 * @return {'real'|'pointsAndMoney'|'pointsOnly'} the modality
 */
export function paymentOptionTypeFromPrice(price) {
	if (!price || !(price.points > 0)) return 'real'

	return price.value > 0 ? 'pointsAndMoney' : 'pointsOnly'
}

/**
 * Finds the payment option the cart is currently on, inside the per-user
 * pricing block. Returns `null` for an anonymous cart (`userPricing` only comes
 * with a Bearer) or when the modality has no matching option.
 * @param {object} cart A MuviHub cart summary
 * @return {object|null} the selected `userPricing.paymentOptions` entry
 */
function selectedPaymentOption(cart) {
	const options =
		cart && cart.userPricing && cart.userPricing.paymentOptions

	return (
		(options &&
			options.find((o) => o.type === cart.selectedPaymentOptionType)) ||
		null
	)
}

/**
 * Builds the `calculatedValues` slice the sale flow reads, from a MuviHub cart
 * summary plus the seats picked so far. Pure: the caller owns dispatching and
 * any side effect.
 *
 * Points totals come from the per-seat data rather than `cart.totals`, which
 * depends on the `selectedOption` resolved in the API's CartMapper (Bug 7905).
 *
 * Subtotal: by the legacy rule (`ComputeTicketCostCommandHandler`), a points
 * purchase converts `tarifa + taxa de embarque + outros + pedagio` as a whole
 * and the cash part is a percentage of that total — so the fees are already
 * inside what the customer pays and there is no "fare without fees" to use as a
 * subtotal. In that case the subtotal is the cash cost of the chosen modality
 * and the fees line is not added on top.
 *
 * @param {object} cart A MuviHub cart summary ({ totals, userPricing, ... })
 * @param {Array} ticketsGoing Seats picked for the outbound leg
 * @param {Array} ticketsReturn Seats picked for the return leg
 * @param {object|null} [previousRebookingContext] The `rebookingContext` already
 * in `calculatedValues`, carried forward when this cart response has none —
 * `setCalculatedValues` replaces the slice wholesale, so dropping it here would
 * silently undo the rebooking charge/refund classification.
 * @return {object} the `calculatedValues` slice
 */
export function buildCalculatedValues(
	cart,
	ticketsGoing = [],
	ticketsReturn = [],
	previousRebookingContext = null
) {
	const mappedTotals = mapCartTotals(cart && cart.totals)
	const enrichedGoing = ticketsGoing.map(enrichTicket)
	const enrichedReturn = ticketsReturn.map(enrichTicket)

	const allSeats = [...enrichedGoing, ...enrichedReturn]
	const sumOf = (field) => allSeats.reduce((sum, t) => sum + (t[field] || 0), 0)

	const totalPointsBpe = sumOf('bpePointsUsed')
	const totalPointsConv = sumOf('convenienceFeePointsUsed')
	const totalPointsIns = sumOf('insurancePointsUsed')
	const totalPointsUsed = totalPointsBpe + totalPointsConv + totalPointsIns
	const isPointsPayment = totalPointsUsed > 0

	// `moneyCost` is the API's own aggregate for the modality; summing the seats
	// is only the fallback for a cart without `userPricing`.
	const selectedOption = selectedPaymentOption(cart)
	const seatsMoneyCost =
		selectedOption && selectedOption.moneyCost != null
			? selectedOption.moneyCost
			: sumOf('fare')

	return {
		...mappedTotals,
		totalPointsBpe: isPointsPayment ? totalPointsBpe : mappedTotals.totalPointsBpe,
		totalPointsForConvenienceFee: isPointsPayment
			? totalPointsConv
			: mappedTotals.totalPointsForConvenienceFee,
		totalInsurancePoints: isPointsPayment
			? totalPointsIns
			: mappedTotals.totalInsurancePoints,
		totalPointsUsed: isPointsPayment ? totalPointsUsed : mappedTotals.totalPointsUsed,
		subtotalValue: isPointsPayment ? seatsMoneyCost : mappedTotals.subtotalValue,
		feesIncludedInSubtotal: isPointsPayment,
		rebookingContext: resolveRebookingContext(
			cart && cart.rebookingContext,
			previousRebookingContext
		),
		ticketsGoing: enrichedGoing,
		ticketsReturn: enrichedReturn,
	}
}
